import { useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { Tooltip, Typography } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { QuoteNotFoundError } from '@root/entities/quote/model/quote-error'
import { AmountFormatted } from '@root/shared/core/format'
import { environmentAtom } from '@root/shared/environment'
import { useFormatDuration } from '@root/shared/hooks'
import { dateBySecondAtom, useOptionalAtom } from '@root/shared/lib/reatom'

import type { CurrencyModel } from '@root/entities/currency/model'
import type { PathfinderQuoteSubscriptionModel } from '@root/features/quote/atoms/reatom-quote-subscription'
import type { BigNumber } from 'bignumber.js'

type QuoteMinAmountTooltipProps = {
  model: PathfinderQuoteSubscriptionModel | undefined
  currency: CurrencyModel
  children: React.ReactElement
}

export const QuoteMinAmountTooltip: React.FC<QuoteMinAmountTooltipProps> = ({
  children,
  currency,
  model,
}) => {
  const [{ isDev }] = useAtom(environmentAtom)
  const { formatDuration } = useFormatDuration()

  const [formattedAges] = useAtom((ctx) => {
    const { isDev } = ctx.get(environmentAtom)
    if (!isDev || !model) {
      return undefined
    }

    const quoteState = ctx.spy(model)
    const subscribedAt = ctx.spy(model.subscribedAt)

    const quote = quoteState.value?.quote

    if (!subscribedAt && !quote) {
      return undefined
    }

    const date = ctx.spy(dateBySecondAtom)
    const subscribedAtFormatted = subscribedAt
      ? formatDuration({
          startAt: subscribedAt,
          endAt: date,
          mode: 'singleUnitShort',
        })
      : undefined

    const quoteAgeFormatted = quoteState.value
      ? formatDuration({
          startAt: quoteState.value.timestamp,
          endAt: date,
          mode: 'singleUnitShort',
        })
      : undefined

    const firstQuoteReceivedAt = ctx.spy(model.firstQuoteReceivedAt)
    const firstQuoteReceivedAtFormatted = (() => {
      if (!firstQuoteReceivedAt || !subscribedAt) {
        return undefined
      }

      const diff = firstQuoteReceivedAt.getTime() - subscribedAt.getTime()

      return `${diff}ms`
    })()

    return {
      quoteAge: quoteAgeFormatted,
      subscriptionAge: subscribedAtFormatted,
      firstQuoteReceivedAge: firstQuoteReceivedAtFormatted,
    }
  })
  const quoteState = useOptionalAtom(model)
  const subscribedAt = useOptionalAtom(model?.subscribedAt)

  const minAmountText = useMemo(() => {
    const quote = quoteState?.value?.quote
    if (!quote) {
      return undefined
    }

    return <Body currency={currency} minAmountOut={quote.minimumReceive} />
  }, [currency, quoteState?.value?.quote])

  const tooltipContent = useMemo(() => {
    if (!isDev) {
      return minAmountText
    }

    if (!quoteState) {
      return undefined
    }

    const subscribedAtText = subscribedAt ? (
      <div>Subscription Age: {formattedAges?.subscriptionAge}</div>
    ) : undefined

    const quoteAgeText = formattedAges?.quoteAge ? (
      <div>Quote Age: {formattedAges.quoteAge}</div>
    ) : undefined

    const firstQuoteReceivedAtText = formattedAges?.firstQuoteReceivedAge ? (
      <div>
        First Quote Received Time: {formattedAges.firstQuoteReceivedAge}
      </div>
    ) : undefined

    const content = match(quoteState)
      .with({ type: 'Error' }, ({ error }) =>
        error instanceof QuoteNotFoundError ? 'Quote Not Found' : error.message,
      )
      .with({ type: 'Loading' }, () => subscribedAtText)
      .with({ type: 'Success' }, () => {
        return (
          <>
            <div>{minAmountText}</div>
            {subscribedAtText}
            {quoteAgeText}
            {firstQuoteReceivedAtText}
          </>
        )
      })
      .with({ type: 'NotInitiated' }, () => undefined)
      .exhaustive()

    return content
  }, [formattedAges, isDev, minAmountText, quoteState, subscribedAt])

  if (!tooltipContent) {
    return children
  }

  if (!quoteState) {
    return children
  }

  return <StyledTooltip content={tooltipContent}>{children}</StyledTooltip>
}

const StyledTooltip = styled(Tooltip)`
  padding: 10px;

  border-radius: 8px;
  border: 1px solid var(--backgroundA-3);
  background: var(--background-3);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.65);
`

const Body: React.FC<{
  minAmountOut: BigNumber
  currency: CurrencyModel
}> = ({ currency, minAmountOut }) => {
  const { t } = useTranslation()

  const ticker =
    currency.ticker.length > 5
      ? `${currency.ticker.slice(0, 5)}…`
      : currency.ticker

  return (
    <BodyRoot>
      <CurrencyIcon
        withGoogleLens={false}
        withPreview={false}
        currency={currency}
        outline={'outline'}
        size={30}
      />
      <Info>
        <Label variant="body4-medium">
          {t('trade.quick-buy.min-amount-amount')}
        </Label>
        <Typography variant="body4-medium">
          <AmountFormatted amount={minAmountOut} currency={ticker} />
        </Typography>
      </Info>
    </BodyRoot>
  )
}

const BodyRoot = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  gap: 8px;
`

const Info = styled.div`
  display: grid;
`
const Label = styled(Typography)`
  color: var(--text-10);
`
